import { useState, useEffect } from "react";
import { G, ADMIN_API, adminInput, LEAD_STATUSES, IcoLeads } from "../../design/tokens";
import LeadModal from "./LeadModal";
import LeadStatusBadge from "./LeadStatusBadge";
import StatCard from "./StatCard";

export default function LeadsModule({ authHeader, canWrite, loggedUser }) {
  const [leads, setLeads]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [fetchErr, setFetchErr] = useState("");
  const [viewLead, setViewLead] = useState(null);
  const [statusFilter, setStatusFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");

  const loadLeads = () => {
    setLoading(true);
    fetch(`${ADMIN_API}/leads`, { headers: authHeader })
      .then(async r => {
        const d = await r.json();
        if (!r.ok) { setFetchErr(d.error || `Error ${r.status}`); setLoading(false); return; }
        setLeads(d.leads || []); setLoading(false);
      })
      .catch(e => { setFetchErr(e.message); setLoading(false); });
  };

  useEffect(() => { loadLeads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const now     = new Date();
  const weekAgo = new Date(now - 7 * 86400000);
  const thisWeek  = leads.filter(l => l.createdAt && new Date(l.createdAt) > weekAgo).length;
  const converted = leads.filter(l => l.status === "Converted").length;

  const sourceLabel = (src) => {
    if (src === "chatbot") return "ChatBot";
    if (src === "website-contact") return "Form";
    return src || "—";
  };
  const sourceBadge = (src) => {
    const isChat = src === "chatbot";
    const isForm = src === "website-contact";
    const color  = isChat ? "#06B6D4" : isForm ? "#7C3AED" : "rgba(255,255,255,0.3)";
    const icon   = isChat ? "💬" : isForm ? "📋" : "";
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", borderRadius: 6, background: `${color}14`, border: `1px solid ${color}33`, fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, fontWeight: 600, color, letterSpacing: 0.5 }}>
        {icon} {sourceLabel(src)}
      </span>
    );
  };

  const filtered = leads.filter(l => {
    const matchSearch = [l.name, l.mobile, l.email, String(l.inquiryNo || "")].some(v => (v || "").toLowerCase().includes(search.toLowerCase()));
    const matchStatus = statusFilter === "All" || l.status === statusFilter || (!l.status && statusFilter === "New");
    const matchSource = sourceFilter === "All" || l.source === sourceFilter || (!l.source && sourceFilter === "All");
    return matchSearch && matchStatus && matchSource;
  });

  const TH = { fontFamily: "'Space Grotesk', sans-serif", fontSize: 10, color: "rgba(255,255,255,0.3)", letterSpacing: 2, textTransform: "uppercase", padding: "12px 16px", textAlign: "left", borderBottom: "1px solid rgba(255,255,255,0.04)", fontWeight: 500 };
  const TD = (extra = {}) => ({ fontFamily: "'Space Grotesk', sans-serif", fontSize: 13, color: "#fff", padding: "13px 16px", ...extra });

  return (
    <div style={{ padding: "28px 32px" }}>
      {viewLead && (
        <LeadModal
          lead={viewLead}
          authHeader={authHeader}
          loggedUser={loggedUser || "Admin"}
          onClose={() => setViewLead(null)}
          onSaved={updated => setLeads(prev => prev.map(l => l.leadId === updated.leadId ? updated : l))}
        />
      )}

      <div style={{ display: "flex", gap: 16, marginBottom: 28 }}>
        <StatCard icon={<IcoLeads />} label="Total Leads" value={leads.filter(l => l.leadId !== "__counter__").length} accent="#2563EB" />
        <StatCard icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>} label="This Week" value={thisWeek} accent="#7C3AED" />
        <StatCard icon={<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>} label="Converted" value={converted} accent="#059669" />
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 12, flexWrap: "wrap" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 600, color: "#fff" }}>Leads</div>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…"
          style={adminInput({ width: 180, padding: "7px 12px", marginBottom: 0 })}
        />
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        {["All", ...LEAD_STATUSES].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${statusFilter === s ? "rgba(37,99,235,0.45)" : "rgba(255,255,255,0.06)"}`, background: statusFilter === s ? "rgba(37,99,235,0.13)" : "transparent", color: statusFilter === s ? "#93C5FD" : "rgba(255,255,255,0.35)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, cursor: "pointer", transition: "all 0.15s" }}>{s}</button>
        ))}
        <div style={{ width: 1, height: 18, background: "rgba(255,255,255,0.08)", margin: "0 4px" }} />
        {[["All","All"],["chatbot","💬 ChatBot"],["website-contact","📋 Form"]].map(([val, label]) => (
          <button key={val} onClick={() => setSourceFilter(val)} style={{ padding: "5px 12px", borderRadius: 7, border: `1px solid ${sourceFilter === val ? "rgba(124,58,237,0.45)" : "rgba(255,255,255,0.06)"}`, background: sourceFilter === val ? "rgba(124,58,237,0.13)" : "transparent", color: sourceFilter === val ? "#C4B5FD" : "rgba(255,255,255,0.35)", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, cursor: "pointer", transition: "all 0.15s" }}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.25)", fontSize: 14, padding: "60px 0", textAlign: "center" }}>Loading leads…</div>
      ) : fetchErr ? (
        <div style={{ fontFamily: "'Space Grotesk', sans-serif", color: "#f87171", fontSize: 13, padding: "20px 24px", background: "rgba(248,113,113,0.08)", borderRadius: 12, border: "1px solid rgba(248,113,113,0.18)" }}>Error: {fetchErr}</div>
      ) : (
        <div style={{ background: G.glass, border: `1px solid ${G.glassBrd}`, borderRadius: 16, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "rgba(4,10,24,0.6)" }}>
                {["#","Name","Mobile","Email","Source","Status","Date",""].map(h => <th key={h} style={{ ...TH, ...(h === "#" ? { width: 50 } : {}) }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={8} style={{ fontFamily: "'Space Grotesk', sans-serif", color: "rgba(255,255,255,0.2)", fontSize: 13, padding: "48px", textAlign: "center" }}>No leads found</td></tr>
              ) : filtered.filter(l => l.leadId !== "__counter__").map((l, i) => (
                <tr key={l.leadId || i} style={{ borderBottom: "1px solid rgba(255,255,255,0.03)", transition: "background 0.15s", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(37,99,235,0.06)"}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}
                  onClick={() => setViewLead(l)}
                >
                  <td style={TD({ color: "rgba(255,255,255,0.35)", fontSize: 12 })}>{l.inquiryNo ? `#${l.inquiryNo}` : "—"}</td>
                  <td style={TD({ fontWeight: 500 })}>
                    <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                      <div style={{ width: 30, height: 30, borderRadius: "50%", background: "linear-gradient(135deg, rgba(37,99,235,0.3), rgba(124,58,237,0.3))", border: "1px solid rgba(37,99,235,0.25)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#93C5FD", fontWeight: 600, flexShrink: 0 }}>
                        {(l.name || "?")[0].toUpperCase()}
                      </div>
                      {l.name || "—"}
                    </div>
                  </td>
                  <td style={TD({ color: "rgba(255,255,255,0.5)" })}>{l.mobile || "—"}</td>
                  <td style={TD({ color: "rgba(255,255,255,0.5)" })}>{l.email || "—"}</td>
                  <td style={TD()}>{sourceBadge(l.source)}</td>
                  <td style={TD()}><LeadStatusBadge status={l.status || "New"} /></td>
                  <td style={TD({ color: "rgba(255,255,255,0.3)", fontSize: 12 })}>{l.createdAt ? new Date(l.createdAt).toLocaleDateString() : "—"}</td>
                  <td style={TD()}>
                    <button onClick={e => { e.stopPropagation(); setViewLead(l); }} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid rgba(37,99,235,0.3)", background: "rgba(37,99,235,0.08)", color: "#93C5FD", fontFamily: "'Space Grotesk', sans-serif", fontSize: 11, cursor: "pointer" }}>
                      {canWrite ? "Edit" : "View"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
